import { useCallback, useEffect, useRef, useState } from "react"
import DatalakeService from "src/services/datalake-service"
import EmissaoService from "src/services/emissao-service"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"

const getErrorMessage = (error, fallback) => error?.response?.body?.detail || error?.response?.body?.message || fallback

const useCompanyHistory = ({ definition, company, onChanged }) => {
    const datalakeServiceRef = useRef(new DatalakeService())
    const emissaoServiceRef = useRef(new EmissaoService())
    const requestIdRef = useRef(0)
    const mutationRef = useRef(false)
    const [state, setState] = useState({ status: "idle", rows: [], loadedIdentifier: null })
    const identifier = definition?.getIdentifier(company)

    const load = useCallback(async () => {
        if (!definition || !identifier) {
            setState({ status: "empty", rows: [], loadedIdentifier: null })
            return
        }

        const requestId = requestIdRef.current + 1
        requestIdRef.current = requestId
        setState((current) => ({
            ...current,
            status: "loading",
            rows: current.loadedIdentifier === identifier ? current.rows : [],
            error: null,
        }))

        try {
            const rows = await definition.load({
                datalakeService: datalakeServiceRef.current,
                emissaoService: emissaoServiceRef.current,
                identifier,
            })
            if (requestId !== requestIdRef.current) return
            setState({ status: rows.length ? "success" : "empty", rows, error: null, loadedIdentifier: identifier })
        } catch (error) {
            if (requestId !== requestIdRef.current) return
            setState({ status: "error", rows: [], error: getErrorMessage(error, "Não foi possível carregar o histórico."), loadedIdentifier: null })
        }
    }, [definition, identifier])

    useEffect(() => {
        load()
        return () => { requestIdRef.current += 1 }
    }, [load])

    const mutate = useCallback(async ({ method, row }) => {
        if (!definition?.editable || mutationRef.current) return false
        mutationRef.current = true
        setState((current) => ({ ...current, status: "mutating" }))
        try {
            await definition.editable.request({ emissaoService: emissaoServiceRef.current, method, row })
            dispatchSnackbar(method === "delete" ? "Registro excluído com sucesso." : "Registro salvo com sucesso.", "success")
            await load()
            onChanged?.()
            return true
        } catch (error) {
            dispatchSnackbar(getErrorMessage(error, "Não foi possível salvar o registro."), "error")
            setState((current) => ({ ...current, status: current.rows.length ? "success" : "empty" }))
            return false
        } finally {
            mutationRef.current = false
        }
    }, [definition, load, onChanged])

    const { loadedIdentifier, ...historyState } = state
    const hasStaleRows = historyState.rows.length > 0 && loadedIdentifier !== identifier
    const visibleHistoryState = hasStaleRows
        ? { ...historyState, status: "loading", rows: [] }
        : historyState

    return {
        ...visibleHistoryState,
        identifier,
        loading: visibleHistoryState.status === "loading" || visibleHistoryState.status === "mutating",
        load,
        mutate,
    }
}

export default useCompanyHistory
